import { addComponent } from "bitecs";
import { ASSIST_WINDOW, DT, Team } from "../constants.ts";
import { BARRIERS, CONSTRUCTS, KITS } from "../heroes/roster/index.ts";
import { HeroId, Role, ULT_GAIN, eyeHeight } from "../heroes/types.ts";
import { add, clamp, dist, dot, flatBasis, norm, rayCapsule, scale, sub, type Vec3, viewDir } from "../math.ts";
import { hv, MoveKind, setHv } from "./components.ts";
import { CONSTRUCT_FLAG_BLOCKS, canDamageConstruct, canTargetConstruct, constructRadius, constructSurfaceDist, damageConstruct, rayConstructs } from "./constructs.ts";
import { releaseHeldTarget } from "./held.ts";
import type { Sim } from "./sim.ts";

/** Third-person camera offsets. The client camera uses the same values so the crosshair matches. */
export const CAMERA = { right: 0.7, up: 0.4, back: 3.8 };

/** Slack when looking for a solid construct at the distance where its own obstacle stopped a ray. */
const HIT_EPS = 1e-3;

function withShotObstacles<T>(sim: Sim, f: () => T): T {
  const old = sim.physics.obstacles;
  sim.physics.obstacles = sim.baseObstacles;
  try {
    return f();
  } finally {
    sim.physics.obstacles = old;
  }
}

const shotRaycast = (sim: Sim, shooter: number, origin: Vec3, dir: Vec3, maxDist: number) =>
  withShotObstacles(sim, () => sim.physics.raycast(origin, dir, maxDist));

const shotSphereCast = (sim: Sim, shooter: number, origin: Vec3, dir: Vec3, radius: number, maxDist: number) =>
  withShotObstacles(sim, () => sim.physics.sphereCast(origin, dir, radius, maxDist));

export function eyePos(sim: Sim, eid: number): Vec3 {
  const c = sim.c;
  return { x: c.Pos.x[eid]!, y: c.Pos.y[eid]! + eyeHeight(sim.hero(eid), !!c.Player.crouch[eid]), z: c.Pos.z[eid]! };
}

/** Enemy damageable constructs inside a cone in front of `owner`, nearest first. */
export function coneConstructs(sim: Sim, owner: number, range: number, halfAngle: number) {
  const c = sim.c;
  const origin = eyePos(sim, owner);
  const fwd = viewDir(c.Look.yaw[owner]!, Math.max(-0.3, Math.min(0.3, c.Look.pitch[owner]!)));
  const out: { eid: number; d: number }[] = [];
  for (const e of sim.constructEids()) {
    if (!canDamageConstruct(sim, e, owner)) continue;
    const shape = CONSTRUCTS[c.Construct.kind[e]!]?.shape;
    const h = shape?.h ?? 1;
    const center = add(sim.posOf(e), { x: 0, y: h * 0.5, z: 0 });
    const to = sub(center, origin);
    const d = Math.hypot(to.x, to.y, to.z);
    if (constructSurfaceDist(sim, e, origin) > range) continue;
    const cos = d > 0.01 ? dot(to, fwd) / d : 1;
    if (Math.acos(clamp(cos, -1, 1)) > halfAngle + Math.atan2(constructRadius(sim, e), Math.max(d, 0.1))) continue;
    out.push({ eid: e, d });
  }
  return out.sort((a, b) => a.d - b.d);
}

/** Shoulder-camera position. `scale` grows the offsets for bigger heroes (height / 1.8). */
export function cameraPos(eye: Vec3, yaw: number, pitch: number, scale = 1, back = CAMERA.back * scale): Vec3 {
  const dir = viewDir(yaw, pitch);
  const { right } = flatBasis(yaw);
  return {
    x: eye.x + right.x * CAMERA.right * scale - dir.x * back,
    y: eye.y + CAMERA.up * scale - dir.y * back,
    z: eye.z + right.z * CAMERA.right * scale - dir.z * back,
  };
}

export const cameraScale = (heroHeight: number) => Math.max(1, heroHeight / 1.8) ** 1.5;

/**
 * Crosshair aim: cast from the (deterministic) camera position along the view direction to find
 * the aim point, then fire from the eye toward it. Returns origin + normalized direction.
 * Movement abilities must pass includePlayers=false so client prediction (which has no other
 * players) resolves the same aim point as the server.
 */
export function aim(sim: Sim, eid: number, range = 200, includePlayers = true): { origin: Vec3; dir: Vec3; point: Vec3 } {
  const c = sim.c;
  const yaw = c.Look.yaw[eid]!;
  const pitch = c.Look.pitch[eid]!;
  const eye = eyePos(sim, eid);
  const vdir = viewDir(yaw, pitch);
  const cam = cameraPos(eye, yaw, pitch, cameraScale(sim.hero(eid).height));
  // Start the camera ray level with the player so geometry behind them can't block aim.
  const skip = Math.max(0, dot(sub(eye, cam), vdir));
  const start = add(cam, scale(vdir, skip));
  const t = includePlayers ? shotRaycast(sim, eid, start, vdir, range) : sim.physics.raycast(start, vdir, range);
  let limit = t >= 0 ? t : range;
  if (includePlayers) {
    // Enemy barriers and deployables are what the crosshair is on, so aim at them, not past them:
    // otherwise the shoulder offset sends a shot from the eye wide of anything small.
    const team = c.Player.team[eid]!;
    const bar = rayBarriers(sim, team, start, vdir, limit);
    if (bar) limit = bar.t;
    const con = rayConstructs(sim, start, vdir, limit + HIT_EPS, (e) => canTargetConstruct(sim, e, eid));
    if (con) limit = Math.min(limit, con.t);
  }
  const hp = includePlayers ? rayPlayers(sim, eid, start, vdir, limit) : null;
  const point = add(start, scale(vdir, hp ? hp.t : limit));
  let dir = norm(sub(point, eye));
  if (dist(point, eye) < 1.0) dir = vdir;
  return { origin: eye, dir, point };
}

export const isEnemy = (sim: Sim, a: number, b: number) => sim.c.Player.team[a] !== sim.c.Player.team[b];

// ---------------------------------------------------------------- barriers

interface BarrierHit {
  t: number;
  owner: number;
}

function barrierFrame(sim: Sim, owner: number) {
  const c = sim.c;
  const spec = BARRIERS[c.Player.hero[owner] as HeroId];
  if (!spec) return undefined;
  const { fwd, right } = flatBasis(c.Look.yaw[owner]!);
  const center = {
    x: c.Pos.x[owner]! + fwd.x * spec.forward,
    y: c.Pos.y[owner]! + spec.height / 2 - 0.2,
    z: c.Pos.z[owner]! + fwd.z * spec.forward,
  };
  return { center, normal: fwd, right, spec };
}

/** Nearest enemy barrier crossed by segment origin + dir * [0, maxDist]. */
export function rayBarriers(sim: Sim, team: number, origin: Vec3, dir: Vec3, maxDist: number): BarrierHit | null {
  const c = sim.c;
  let best: BarrierHit | null = null;
  for (const e of sim.playerEids()) {
    const spec = BARRIERS[c.Player.hero[e] as HeroId];
    if (!spec || c.Player.team[e] === team || !c.Player.alive[e]) continue;
    if (!hv(c, e, spec.upVar)) continue;
    const frame = barrierFrame(sim, e);
    if (!frame) continue;
    const { center, normal, right } = frame;
    const denom = dot(dir, normal);
    if (Math.abs(denom) < 1e-5) continue;
    const t = dot(sub(center, origin), normal) / denom;
    if (t < 0 || t > maxDist || (best && t >= best.t)) continue;
    const p = add(origin, scale(dir, t));
    const rel = sub(p, center);
    if (Math.abs(dot(rel, right)) <= spec.width / 2 && Math.abs(rel.y) <= spec.height / 2) best = { t, owner: e };
  }
  return best;
}

export function damageBarrier(sim: Sim, owner: number, amount: number, attacker: number) {
  if (!sim.authority) return;
  const c = sim.c;
  const spec = BARRIERS[c.Player.hero[owner] as HeroId];
  if (!spec) return;
  const hp = hv(c, owner, spec.hpVar) - amount;
  setHv(c, owner, spec.hpVar, Math.max(0, hp));
  if (spec.regenDelay !== undefined) setHv(c, owner, spec.upVar + 2, spec.regenDelay);
  const meta = sim.players.get(owner);
  if (meta) meta.stats.mitigated += amount;
  addUlt(sim, attacker, amount * 0.5, "damage");
  if (hp <= 0) {
    setHv(c, owner, spec.upVar, 0);
    if (spec.regenDelay !== undefined) setHv(c, owner, spec.upVar + 2, 300);
  }
}

// ---------------------------------------------------------------- hitscan

export interface HitscanOpts {
  range: number;
  damage: number;
  falloffStart?: number;
  falloffEnd?: number;
  falloffMin?: number;
  canCrit?: boolean;
  /** Headshot multiplier; defaults to CRIT_MUL. */
  critMul?: number;
  fxKind: number;
  /** Emit tracer event (false for pellets beyond the first few). */
  tracer?: boolean;
  /** Continue through this many additional players/damageable constructs. */
  pierce?: number;
}

export interface HitResult {
  end: Vec3;
  target?: number;
  head?: boolean;
  dealt: number;
}

/** Nearest enemy hitbox along a ray, using lag-compensated positions. */
export function rayPlayers(sim: Sim, shooter: number, origin: Vec3, dir: Vec3, maxDist: number, allies = false, ignore: Set<number> = new Set()) {
  const c = sim.c;
  let best: { t: number; target: number; head: boolean } | null = null;
  if (!sim.authority) return best;
  for (const e of sim.playerEids()) {
    if (e === shooter || ignore.has(e)) continue;
    if (allies ? isEnemy(sim, shooter, e) : !isEnemy(sim, shooter, e)) continue;
    if (c.Status.phased[e]! > 0) continue;
    const hb = sim.hitCapsules(e);
    if (!hb.alive) continue;
    for (const cap of hb.capsules) {
      const t = rayCapsule(origin, dir, cap.a, cap.b, cap.r, maxDist);
      if (t >= 0 && (!best || t < best.t || (Math.abs(t - best.t) < 0.05 && cap.head))) best = { t, target: e, head: cap.head };
    }
  }
  return best;
}

/** Nearest hitbox of any team (e.g. a shot that heals allies and damages enemies). */
export function rayAnyPlayer(sim: Sim, shooter: number, origin: Vec3, dir: Vec3, maxDist: number) {
  const a = rayPlayers(sim, shooter, origin, dir, maxDist, false);
  const b = rayPlayers(sim, shooter, origin, dir, maxDist, true);
  return !a ? b : !b ? a : a.t <= b.t ? a : b;
}

export function hitscan(sim: Sim, shooter: number, origin: Vec3, dir: Vec3, o: HitscanOpts): HitResult {
  const c = sim.c;
  let limit = shotRaycast(sim, shooter, origin, dir, o.range);
  if (limit < 0) limit = o.range;
  const team = c.Player.team[shooter]!;
  const barrier = rayBarriers(sim, team, origin, dir, limit);
  const construct = rayConstructs(
    sim,
    origin,
    dir,
    (barrier ? Math.min(limit, barrier.t) : limit) + HIT_EPS,
    (e) => canTargetConstruct(sim, e, shooter),
  );
  if (barrier) limit = barrier.t;
  if (construct) limit = construct.t;
  const res: HitResult = { end: add(origin, scale(dir, limit)), dealt: 0 };
  const ignored = new Set<number>();
  let pierceLeft = o.pierce ?? 0;
  for (;;) {
    const hit = rayPlayers(sim, shooter, origin, dir, limit, false, ignored);
    const con = rayConstructs(
      sim,
      origin,
      dir,
      (hit ? Math.min(limit, hit.t) : limit) + HIT_EPS,
      (e) => !ignored.has(e) && canTargetConstruct(sim, e, shooter),
    );
    if (!hit && !con) break;
    const t = con ? con.t : hit!.t;
    const end = con ? con.pos : add(origin, scale(dir, t));
    let dmg = o.damage;
    if (o.falloffStart !== undefined && o.falloffEnd !== undefined) {
      const k = clamp((t - o.falloffStart) / (o.falloffEnd - o.falloffStart), 0, 1);
      dmg *= 1 - k * (1 - (o.falloffMin ?? 0.5));
    }
    if (con) {
      res.dealt += damageConstruct(sim, con.eid, dmg, shooter);
      ignored.add(con.eid);
      if (res.target === undefined) res.target = con.eid;
      if (c.Construct.flags[con.eid]! & CONSTRUCT_FLAG_BLOCKS) break;
    } else {
      res.head = hit!.head && !!o.canCrit;
      res.dealt += applyDamage(sim, hit!.target, dmg, shooter, { crit: res.head, critMul: o.critMul, pos: end, info: { kind: "hitscan", crit: !!res.head, sourceKind: o.fxKind } });
      ignored.add(hit!.target);
      if (res.target === undefined) res.target = hit!.target;
    }
    res.end = end;
    if (pierceLeft-- <= 0) break;
  }
  if (res.target === undefined && construct && !ignored.has(construct.eid)) {
    res.end = construct.pos;
    res.target = construct.eid;
    res.dealt = damageConstruct(sim, construct.eid, o.damage, shooter);
  } else if (barrier) {
    damageBarrier(sim, barrier.owner, o.damage, shooter);
  }
  if (o.tracer !== false) {
    const muzzle = add(origin, add(scale(dir, 0.6), scale(flatBasis(c.Look.yaw[shooter]!).right, 0.25)));
    sim.emit({ type: "tracer", owner: c.Net.id[shooter]!, from: muzzle, to: res.end, kind: o.fxKind });
  }
  return res;
}

// ---------------------------------------------------------------- damage & healing

/** Rivals' default headshot multiplier, for every role; some weapons set their own (critMul). */
export const CRIT_MUL = 2;

export interface DamageOpts {
  crit?: boolean;
  /** Headshot multiplier; defaults to CRIT_MUL. */
  critMul?: number;
  pos?: Vec3;
  info?: Partial<DamageInfo>;
  /** Skip the sleep-wake and knockback side effects. */
  quiet?: boolean;
}

export interface DamageInfo {
  kind: "hitscan" | "projectile" | "explosion" | "melee" | "aura" | "dot" | "other";
  crit?: boolean;
  sourceKind?: number;
  redirected?: boolean;
}

export interface DamageFilterSpec {
  seconds: number;
  tag?: string;
  filter(sim: Sim, target: number, amount: number, attacker: number | undefined, info: DamageInfo): number;
  onExpire?(sim: Sim, target: number): void;
}

interface ActiveDamageFilter extends DamageFilterSpec {
  owner: number;
  ticks: number;
  seq: number;
}

const damageFilters = new WeakMap<Sim, Map<number, ActiveDamageFilter[]>>();
let filterSeq = 1;

const filtersFor = (sim: Sim, target: number) => {
  let m = damageFilters.get(sim);
  if (!m) {
    m = new Map();
    damageFilters.set(sim, m);
  }
  let list = m.get(target);
  if (!list) {
    list = [];
    m.set(target, list);
  }
  return list;
};

/** Adds a server-side damage interception effect to a target; newest filters run first. */
export function addDamageFilter(sim: Sim, target: number, owner: number, spec: DamageFilterSpec) {
  filtersFor(sim, target).push({ ...spec, owner, ticks: Math.round(spec.seconds * 60), seq: filterSeq++ });
}

/** Removes filters on `target` owned by `owner`; optionally only a matching `tag`. */
export function removeDamageFilters(sim: Sim, target: number, owner: number, tag?: string) {
  const list = damageFilters.get(sim)?.get(target);
  if (!list) return;
  for (let i = list.length - 1; i >= 0; i--) if (list[i]!.owner === owner && (tag === undefined || list[i]!.tag === tag)) list.splice(i, 1);
}

/** Ticks damage filters and runs expiry callbacks. */
export function stepDamageFilters(sim: Sim) {
  const m = damageFilters.get(sim);
  if (!m) return;
  for (const [target, list] of m) {
    for (let i = list.length - 1; i >= 0; i--) {
      const f = list[i]!;
      f.ticks--;
      if (f.ticks <= 0) {
        list.splice(i, 1);
        f.onExpire?.(sim, target);
      }
    }
    if (!list.length) m.delete(target);
  }
}

/**
 * Grants ult energy for `points` of damage or healing dealt, at the hero's conversion rate
 * (ULT_GAIN), or for `points` of energy outright ("energy", e.g. passive gain).
 */
export function addUlt(sim: Sim, eid: number | undefined, points: number, kind: "damage" | "heal" | "energy") {
  if (eid === undefined || !sim.authority) return;
  const c = sim.c;
  if (c.Ult.active[eid]! > 0 || !sim.players.has(eid)) return;
  const def = sim.hero(eid);
  const energy = kind === "energy" ? points : points * (def.ultGain?.[kind] ?? ULT_GAIN[def.role][kind]);
  c.Ult.charge[eid] = Math.min(1, c.Ult.charge[eid]! + energy / def.ultCost);
}

/** Ult energy per second while a round is in progress. */
export const passiveUltRate = (sim: Sim, eid: number) => {
  const def = sim.hero(eid);
  return def.ultGain?.passive ?? ULT_GAIN[def.role].passive;
};

/** Gives barrier-style mitigation stat and ult credit for absorbed damage. */
export function creditMitigation(sim: Sim, owner: number | undefined, amount: number) {
  if (owner === undefined || amount <= 0 || !sim.authority || !sim.players.has(owner)) return;
  sim.players.get(owner)!.stats.mitigated += amount;
  addUlt(sim, owner, amount * 0.5, "damage");
}

let echoing = false;

/** Applies damage through overhealth → shields → armor → health. Returns amount dealt. */
export function applyDamage(sim: Sim, target: number, base: number, attacker: number | undefined, o: DamageOpts = {}): number {
  if (!sim.authority) return 0;
  const c = sim.c;
  if (!c.Player.alive[target] || c.Status.invuln[target]! > 0 || c.Status.phased[target]! > 0) return 0;
  const info: DamageInfo = { kind: "other", crit: !!o.crit, ...o.info };
  let amount = base;
  if (info.crit) amount *= o.critMul ?? CRIT_MUL;
  if (attacker !== undefined && c.Status.boost[attacker]! > 0) amount *= 1 + c.Status.boostPct[attacker]!;
  if (c.Status.resist[target]! > 0) amount *= 1 - c.Status.resistPct[target]!;
  if (sim.damageFilter) amount = sim.damageFilter(target, amount);
  if (!info.redirected) {
    const list = (damageFilters.get(sim)?.get(target) ?? []).slice().sort((a, b) => b.seq - a.seq);
    for (const f of list) {
      const before = amount;
      amount = Math.max(0, f.filter(sim, target, amount, attacker, info));
      creditMitigation(sim, f.owner, before - amount);
      if (amount <= 0) break;
    }
  }
  if (amount > 0) amount = Math.max(0, KITS[c.Player.hero[target] as HeroId].onDamaged?.(sim, target, amount, attacker, info) ?? amount);
  if (amount <= 0) return 0;
  c.Status.sleep[target] = 0;

  let remaining = amount;
  const H = c.Health;
  const take = (pool: Float32Array, mul = 1) => {
    if (remaining <= 0 || pool[target]! <= 0) return;
    const eff = remaining * mul;
    const used = Math.min(pool[target]!, eff);
    pool[target] = pool[target]! - used;
    remaining -= used / mul;
  };
  take(H.over);
  take(H.shield);
  if (H.armor[target]! > 0) take(H.armor, 0.7);
  take(H.hp);
  const dealt = amount - Math.max(0, remaining);
  H.sinceDamage[target] = 0;

  const vm = sim.players.get(target);
  if (attacker !== undefined && attacker !== target) {
    const am = sim.players.get(attacker);
    if (am) am.stats.damage += dealt;
    addUlt(sim, attacker, dealt, "damage");
    if (vm) vm.damagedBy.set(attacker, sim.tick);
    KITS[c.Player.hero[attacker] as HeroId].dealtDamage?.(sim, attacker, target, dealt);
    KITS[c.Player.hero[attacker] as HeroId].onDealtDamage?.(sim, attacker, target, dealt, info);
  }
  sim.emit({
    type: "damage",
    attacker: attacker !== undefined ? c.Net.id[attacker]! : 0,
    target: c.Net.id[target]!,
    amount: Math.round(dealt),
    crit: !!info.crit,
    pos: o.pos ?? add(sim.posOf(target), { x: 0, y: sim.hero(target).height * 0.6, z: 0 }),
  });
  if (c.Status.linked[target]! > 0 && !echoing && dealt > 0) {
    echoing = true;
    try {
      for (const o of sim.playerEids())
        if (o !== target && c.Status.linked[o]! > 0 && c.Player.team[o] === c.Player.team[target]) applyDamage(sim, o, dealt * c.Status.linkedPct[o]!, attacker, { quiet: true, info: { kind: "other", redirected: true } });
    } finally {
      echoing = false;
    }
  }
  if (H.hp[target]! <= 0.001) {
    const prevented = KITS[c.Player.hero[target] as HeroId].onLethal?.(sim, target, attacker) ?? false;
    if (!prevented && H.hp[target]! <= 0.001) sim.kill(target, attacker);
  }
  return dealt;
}

export function applyHeal(sim: Sim, healer: number, target: number, amount: number, o: { silent?: boolean; noUlt?: boolean } = {}): number {
  if (!sim.authority) return 0;
  const c = sim.c;
  if (!c.Player.alive[target]) return 0;
  if (c.Status.antiHeal[target]! > 0) amount *= Math.max(0, 1 - c.Status.antiHealPct[target]!);
  if (amount <= 0) return 0;
  if (c.Status.healBoost[target]! > 0) amount *= 1 + c.Status.healBoostPct[target]!;
  const H = c.Health;
  let left = amount;
  const fill = (pool: Float32Array, max: Float32Array) => {
    const room = max[target]! - pool[target]!;
    if (room <= 0 || left <= 0) return;
    const used = Math.min(room, left);
    pool[target] = pool[target]! + used;
    left -= used;
  };
  fill(H.hp, H.maxHp);
  fill(H.armor, H.maxArmor);
  fill(H.shield, H.maxShield);
  const healed = amount - left;
  if (healed <= 0) return 0;
  if (healer !== target && c.Player.team[healer] === c.Player.team[target]) enabled(sim, healer, target, "supportedBy", ASSIST_WINDOW);
  if (!o.noUlt) addUlt(sim, healer, healed, "heal");
  KITS[c.Player.hero[target] as HeroId].onHealed?.(sim, target, healer, healed);
  KITS[c.Player.hero[healer] as HeroId].onDealtHeal?.(sim, healer, target, healed);
  if (healer !== target || !o.noUlt) {
    const m = sim.players.get(healer);
    if (m && !o.noUlt) m.stats.healing += healed;
  }
  if (!o.silent && healed >= 1)
    sim.emit({ type: "heal", healer: c.Net.id[healer]!, target: c.Net.id[target]!, amount: Math.round(healed) });
  return healed;
}

export function addOverhealth(sim: Sim, target: number, amount: number, ticks: number, source?: number) {
  const c = sim.c;
  c.Health.over[target] = Math.max(c.Health.over[target]!, amount);
  c.Health.overTicks[target] = Math.max(c.Health.overTicks[target]!, ticks);
  if (source !== undefined && source !== target) enabled(sim, source, target, "supportedBy", Math.max(ticks, ASSIST_WINDOW));
}

/**
 * Remembers that `source` helped (or hindered) `target` until `ticks` from now, for assist credit.
 * Authoritative only; prediction sims keep no player ledgers.
 */
function enabled(sim: Sim, source: number, target: number, ledger: "supportedBy" | "disabledBy", ticks: number) {
  const m = sim.players.get(target);
  if (!sim.authority || !m || !sim.players.has(source)) return;
  const until = sim.tick + ticks;
  if ((m[ledger].get(source) ?? -1) < until) m[ledger].set(source, until);
}

export function applyImpulse(sim: Sim, target: number, v: Vec3) {
  if (!sim.authority && target !== undefined && !sim.players.has(target)) return;
  const c = sim.c;
  if (c.Move.kind[target] === MoveKind.Charge) return;
  const k = sim.hero(target).role === Role.Tank ? 0.5 : 1;
  c.Ext.x[target] = c.Ext.x[target]! + v.x * k;
  c.Ext.z[target] = c.Ext.z[target]! + v.z * k;
  if (v.y !== 0) {
    c.Vel.y[target] = Math.max(c.Vel.y[target]!, 0) + v.y * k;
    c.Player.grounded[target] = 0;
  }
}

export type StatusKind =
  | "stun" | "root" | "slow" | "sleep" | "knockdown" | "boost" | "invuln" | "phased" | "antiHeal" | "healBoost" | "resist" | "linked"
  | "stealth" | "haste" | "unstoppable" | "marked" | "bonded" | "frozen" | "held";
const BENEFICIAL = new Set<StatusKind>(["boost", "invuln", "phased", "healBoost", "resist", "stealth", "haste", "unstoppable", "bonded"]);
const STOPPED_BY_UNSTOPPABLE = new Set<StatusKind>(["stun", "root", "slow", "sleep", "knockdown", "frozen", "held"]);

/** Magnitudes used when a kit applies these statuses without its own. */
export const STATUS_DEFAULT_PCT = { boost: 0.3, resist: 0.5, healBoost: 0.5, linked: 0.4, antiHeal: 1 } as const;

export interface StatusOpts {
  slowPct?: number;
  hastePct?: number;
  /** Damage dealt bonus for "boost". */
  boostPct?: number;
  /** Damage taken reduction for "resist". */
  resistPct?: number;
  /** Healing received bonus for "healBoost". */
  healBoostPct?: number;
  /** Share of damage echoed to the other linked targets for "linked". */
  linkedPct?: number;
  /** Healing received reduction for "antiHeal" (default 1: no healing). */
  antiHealPct?: number;
  source?: number;
  by?: number;
}

/**
 * `source` is who applied it, for assist credit: a buff on an ally who then gets a KO, or crowd
 * control on an enemy who's then KO'd, earns the source an assist. While several applications of
 * a status overlap, the strongest still-running magnitude holds.
 */
export function applyStatus(sim: Sim, target: number, kind: StatusKind, seconds: number, o: StatusOpts = {}) {
  const c = sim.c;
  if (!c.Player.alive[target]) return;
  if (!BENEFICIAL.has(kind) && c.Status.invuln[target]! > 0) return;
  if (c.Status.unstoppable[target]! > 0 && STOPPED_BY_UNSTOPPABLE.has(kind)) return;
  const ticks = Math.round(seconds * 60);
  const slowPct = o.slowPct ?? 0;
  if (o.source !== undefined && o.source !== target) {
    const ally = c.Player.team[o.source] === c.Player.team[target];
    if (BENEFICIAL.has(kind) === ally) enabled(sim, o.source, target, ally ? "supportedBy" : "disabledBy", Math.max(ticks, ASSIST_WINDOW));
  }
  c.Status[kind][target] = Math.max(c.Status[kind][target]!, ticks);
  if (kind === "unstoppable") releaseHeldTarget(sim, target);
  if (kind === "slow") c.Status.slowPct[target] = Math.max(slowPct, c.Status.slow[target]! > ticks ? c.Status.slowPct[target]! : 0);
  if (kind === "haste") c.Status.hastePct[target] = Math.max(o.hastePct ?? 0, c.Status.haste[target]! > ticks ? c.Status.hastePct[target]! : 0);
  if (kind === "boost" || kind === "resist" || kind === "healBoost" || kind === "linked" || kind === "antiHeal") {
    const pct = c.Status[`${kind}Pct`];
    pct[target] = Math.max(o[`${kind}Pct`] ?? STATUS_DEFAULT_PCT[kind], c.Status[kind][target]! > ticks ? pct[target]! : 0);
  }
  if (kind === "marked") c.Status.markedBy[target] = (o.by ?? o.source ?? -1) + 1;
  if (kind === "bonded") c.Status.bondedBy[target] = (o.by ?? o.source ?? -1) + 1;
  if (kind === "held") c.Status.heldBy[target] = (o.by ?? o.source ?? -1) + 1;
  if ((kind === "stun" || kind === "sleep" || kind === "knockdown" || kind === "frozen" || kind === "held") && c.Move.kind[target] !== MoveKind.Pulled) {
    c.Move.ticks[target] = 0;
    c.Move.kind[target] = MoveKind.None;
  }
}

// ---------------------------------------------------------------- areas

export function playersInRadius(sim: Sim, pos: Vec3, radius: number, filter: (e: number) => boolean, los = true): { eid: number; d: number }[] {
  const c = sim.c;
  const out: { eid: number; d: number }[] = [];
  for (const e of sim.playerEids()) {
    if (!c.Player.alive[e] || !filter(e)) continue;
    const def = sim.hero(e);
    const center = { x: c.Pos.x[e]!, y: c.Pos.y[e]! + def.height * 0.5, z: c.Pos.z[e]! };
    const d = Math.max(0, dist(center, pos) - def.radius);
    if (d > radius) continue;
    if (los && !sim.physics.los(pos, center)) continue;
    out.push({ eid: e, d });
  }
  return out;
}

export interface ExplodeOpts {
  radius: number;
  damage: number;
  minDamage?: number;
  selfDamageMul?: number;
  knockback?: number;
  selfKnockback?: number;
  skip?: number;
  fxKind: number;
}

export function explode(sim: Sim, owner: number, pos: Vec3, o: ExplodeOpts) {
  const c = sim.c;
  sim.emit({ type: "explosion", pos, radius: o.radius, kind: o.fxKind });
  const probe = pos;
  for (const { eid, d } of playersInRadius(sim, probe, o.radius, (e) => e !== o.skip, true)) {
    const k = 1 - d / o.radius;
    const self = eid === owner;
    if (!self && !isEnemy(sim, owner, eid)) continue;
    const dmg = ((o.minDamage ?? 0) + (o.damage - (o.minDamage ?? 0)) * k) * (self ? (o.selfDamageMul ?? 0) : 1);
    if (dmg > 0) applyDamage(sim, eid, dmg, owner, { pos: add(sim.posOf(eid), { x: 0, y: 1, z: 0 }), info: { kind: "explosion", sourceKind: o.fxKind } });
    const kb = self ? (o.selfKnockback ?? 0) : (o.knockback ?? 0);
    if (kb > 0) {
      const center = add(sim.posOf(eid), { x: 0, y: sim.hero(eid).height * 0.5, z: 0 });
      const dir = norm(sub(center, pos));
      applyImpulse(sim, eid, { x: dir.x * kb * k, y: Math.max(0.35, dir.y) * kb * k, z: dir.z * kb * k });
    }
  }
  for (const { eid, d } of constructsInExplosion(sim, owner, probe, o.radius, o.skip)) {
    const k = 1 - d / o.radius;
    const dmg = (o.minDamage ?? 0) + (o.damage - (o.minDamage ?? 0)) * k;
    if (dmg > 0) damageConstruct(sim, eid, dmg, owner);
  }
  void c;
}

function constructsInExplosion(sim: Sim, owner: number, pos: Vec3, radius: number, skip?: number) {
  const out: { eid: number; d: number }[] = [];
  for (const e of sim.constructEids()) {
    if (e === skip || !canDamageConstruct(sim, e, owner)) continue;
    const d = constructSurfaceDist(sim, e, pos);
    if (d <= radius) out.push({ eid: e, d });
  }
  return out.sort((a, b) => a.d - b.d);
}

/** Enemies in a cone in front of `owner` (uses lag-compensated positions). */
export function coneTargets(sim: Sim, owner: number, range: number, halfAngle: number, filter: (e: number) => boolean) {
  const c = sim.c;
  const origin = eyePos(sim, owner);
  const fwd = viewDir(c.Look.yaw[owner]!, Math.max(-0.3, Math.min(0.3, c.Look.pitch[owner]!)));
  const out: number[] = [];
  if (!sim.authority) return out;
  for (const e of sim.playerEids()) {
    if (e === owner || !filter(e)) continue;
    const hb = sim.hitCapsules(e);
    if (!hb.alive) continue;
    const def = sim.hero(e);
    const center = add(hb.pos, { x: 0, y: def.height * 0.5, z: 0 });
    const to = sub(center, origin);
    const d = Math.hypot(to.x, to.y, to.z);
    if (d - def.radius > range) continue;
    const cos = d > 0.01 ? dot(to, fwd) / d : 1;
    if (Math.acos(clamp(cos, -1, 1)) > halfAngle + Math.atan2(def.radius, Math.max(d, 0.1))) continue;

    out.push(e);
  }
  return out;
}

/** Best target near the crosshair within range (ally or enemy), lag compensated. */
export function lockOn(sim: Sim, owner: number, range: number, maxAngle: number, allies: boolean): number | undefined {
  const c = sim.c;
  if (!sim.authority) return undefined;
  const a = aim(sim, owner, range);
  let best: number | undefined;
  let bestAng = maxAngle;
  for (const e of sim.playerEids()) {
    if (e === owner) continue;
    if (allies === isEnemy(sim, owner, e)) continue;
    const hb = sim.hitCapsules(e);
    if (!hb.alive) continue;
    const center = add(hb.pos, { x: 0, y: sim.hero(e).height * 0.55, z: 0 });
    const to = sub(center, a.origin);
    const d = Math.hypot(to.x, to.y, to.z);
    if (d > range) continue;
    const ang = Math.acos(clamp(dot(to, a.dir) / (d || 1), -1, 1));
    if (ang < bestAng && sim.physics.los(a.origin, center)) {
      bestAng = ang;
      best = e;
    }
  }
  void c;
  return best;
}

// ---------------------------------------------------------------- projectiles & zones

export interface ProjectileSpec {
  kind: number;
  origin: Vec3;
  vel: Vec3;
  gravity?: number;
  radius?: number;
  seconds: number;
  target?: number;
  seq?: number;
}

const projectileHits = new WeakMap<Sim, Map<number, Set<number>>>();
const hitSet = (sim: Sim, p: number) => {
  let m = projectileHits.get(sim);
  if (!m) {
    m = new Map();
    projectileHits.set(sim, m);
  }
  let s = m.get(p);
  if (!s) {
    s = new Set();
    m.set(p, s);
  }
  return s;
};

export function clearProjectilePierce(sim: Sim, p: number) {
  projectileHits.get(sim)?.delete(p);
}

/**
 * Spawns an authoritative projectile (server) or a predicted visual one (client). Callers
 * fast-forward server projectiles by the shooter's view latency (see kits ffTicks).
 */
export function spawnProjectile(sim: Sim, owner: number, s: ProjectileSpec): number | undefined {
  const c = sim.c;
  if (!sim.authority) {
    if (!sim.replaying) sim.predictedProjectiles.push({ ...s, owner, seq: s.seq ?? 0, age: 0 });
    return undefined;
  }
  const p = sim.createEntity();
  addComponent(sim.world, p, c.IsProjectile);
  c.Pos.x[p] = s.origin.x;
  c.Pos.y[p] = s.origin.y;
  c.Pos.z[p] = s.origin.z;
  c.Vel.x[p] = s.vel.x;
  c.Vel.y[p] = s.vel.y;
  c.Vel.z[p] = s.vel.z;
  c.Projectile.kind[p] = s.kind;
  c.Projectile.owner[p] = owner;
  c.Projectile.team[p] = c.Player.team[owner]!;
  c.Projectile.gravity[p] = s.gravity ?? 0;
  c.Projectile.radius[p] = s.radius ?? 0.1;
  c.Projectile.ticks[p] = Math.round(s.seconds * 60);
  c.Projectile.target[p] = s.target !== undefined ? s.target + 1 : 0;
  c.Projectile.seq[p] = s.seq ?? 0;
  return p;
}

export interface ProjectileHit {
  pos: Vec3;
  normal?: Vec3;
  target?: number;
  construct?: number;
  head?: boolean;
  barrier?: number;
  map: boolean;
}

/**
 * Integrates a projectile for one tick and returns the first thing it hit (map, barrier or
 * player). `mode` picks which players it can connect with (supports hit allies).
 */
export type HitMode = "enemies" | "allies" | "both";
export function moveProjectile(sim: Sim, p: number, mode: HitMode = "enemies"): ProjectileHit | null {
  const c = sim.c;
  c.Vel.y[p] = c.Vel.y[p]! - c.Projectile.gravity[p]! * DT;
  const origin = sim.posOf(p);
  const v = { x: c.Vel.x[p]! * DT, y: c.Vel.y[p]! * DT, z: c.Vel.z[p]! * DT };
  const segLen = Math.hypot(v.x, v.y, v.z);
  if (segLen < 1e-6) return null;
  const dir = scale(v, 1 / segLen);
  const r = c.Projectile.radius[p]!;
  let best: ProjectileHit | null = null;
  let bestT = segLen;
  const owner = c.Projectile.owner[p]!;
  const team = c.Projectile.team[p]!;
  const m = r > 0.05 ? shotSphereCast(sim, owner, origin, dir, r, segLen) : null;
  const mt = m ? m.toi : r > 0.05 ? -1 : shotRaycast(sim, owner, origin, dir, segLen);
  if (mt >= 0 && mt <= bestT) {
    bestT = mt;
    best = { pos: add(origin, scale(dir, mt)), normal: m?.normal, map: true };
  }
  const pierced = hitSet(sim, p);
  const bar = rayBarriers(sim, team, origin, dir, bestT);
  if (bar) {
    bestT = bar.t;
    best = { pos: add(origin, scale(dir, bar.t)), barrier: bar.owner, map: false };
  }
  const construct = rayConstructs(
    sim,
    origin,
    dir,
    bestT + r + 1e-4,
    (e) => !pierced.has(e) && canTargetConstruct(sim, e, owner),
    r,
  );
  if (construct) {
    bestT = construct.t;
    best = { pos: construct.pos, construct: construct.eid, map: false };
  }
  for (const e of sim.playerEids()) {
    if (e === owner || pierced.has(e) || !c.Player.alive[e] || c.Status.phased[e]! > 0) continue;
    const ally = c.Player.team[e] === team;
    if (ally ? mode === "enemies" : mode === "allies") continue;
    const hb = sim.hitCapsules(e);
    for (const cap of hb.capsules) {
      const t = rayCapsule(origin, dir, cap.a, cap.b, cap.r + r, bestT);
      if (t >= 0 && t <= bestT) {
        bestT = t;
        best = { pos: add(origin, scale(dir, t)), target: e, head: cap.head, map: false };
      }
    }
  }
  const end = best ? best.pos : add(origin, v);
  c.Pos.x[p] = end.x;
  c.Pos.y[p] = end.y;
  c.Pos.z[p] = end.z;
  c.Projectile.ticks[p] = c.Projectile.ticks[p]! - 1;
  if (best?.target !== undefined) pierced.add(best.target);
  if (best?.construct !== undefined) pierced.add(best.construct);
  return best;
}

export function spawnZone(sim: Sim, owner: number, kind: number, pos: Vec3, radius: number, seconds: number): number | undefined {
  if (!sim.authority) return undefined;
  const c = sim.c;
  const z = sim.createEntity();
  addComponent(sim.world, z, c.IsZone);
  c.Pos.x[z] = pos.x;
  c.Pos.y[z] = pos.y;
  c.Pos.z[z] = pos.z;
  c.Zone.kind[z] = kind;
  c.Zone.owner[z] = owner;
  c.Zone.team[z] = c.Player.team[owner]!;
  c.Zone.radius[z] = radius;
  c.Zone.ticks[z] = Math.round(seconds * 60);
  return z;
}

export const teamOf = (sim: Sim, eid: number) => sim.c.Player.team[eid] as Team;
